import { TargetingSearchResultApiModel } from '../../../api/targeting/model/targeting-search-result.api.model';
import { EntityType } from '../../../api/targeting/enum/entity.type.enum';
import { EntityIdApiModel } from '../../../api/targeting/model/entity-id-api-model';
import { LocalizedEnum } from '../../../api/journodex/model/localized-enum';
import { ChipModel } from '../../../Common/Forms/models/chip.model';
import { TargetingList } from './targeting-list.model';
import { EmploymentModel } from './employment.model';
import { OutletModel } from './outlet.model';
import { CirculationType, CirculationModel } from './circulation.model';
import { ContactModel } from '../../../Common/TargetingShared/model/contact.model';
import { DistributionSummaryModel } from '../../../Common/TargetingShared/model/distribution-summary-model';
import { TargetingMediaTypeEnum } from '../enums/targeting-media-type.enum';

export class Badge {
    iconClass: string;
    tooltip: string;
}

export class TargetingResultModel {
    // 30 days
    static readonly FRESHNESS_THRESHOLD_MS = 30 * 24 * 60 * 60 * 1000;
    // 10 years
    static readonly VETERAN_THRESHOLD_MS = 10 * 365 * 24 * 60 * 60 * 1000;

    entityType?: EntityType;
    id?: any; // Outlet/Contact are strings and employment is number
    name?: string;
    contactName?: string;
    firstName: string;
    lastName: string;
    avatarUrl?: string;
    // uniqueVisitors?: CirculationModel;
    circulations?: CirculationModel[];
    locationString?: string;
    outletId?: string;
    contactId?: string;
    outletName?: string;
    employmentId: number;
    coverageFocus: LocalizedEnum;
    mediaTypes: LocalizedEnum[];

    subjectChips: ChipModel[] = []; // Used in Staff-Search template for chips-container

    loadedEmployments: EmploymentModel[];

    numberOfEmployments: number;

    // Derived - not part of API model
    hasActiveNotice: boolean = false;
    showRemoveDistribution: boolean = false;
    removeDistributionTooltips: string;

    badges: Badge[];

    listMemberships: TargetingList[];
    listMembershipTooltip: string;
    listMembershipChips?: ChipModel[] = [];

    selectedEmployment: EmploymentModel;

    isPrivate = false;

    // @TODO - figure out relationship between targeting result / contact /outlet model. Duplicated properties exist
    contactModel: ContactModel;
    outletModel: OutletModel;

    constructor(
        apiResult?: TargetingSearchResultApiModel
    ) {
        if (!apiResult) {
            return;
        }

        this.entityType = apiResult.entityType;

        if (apiResult.entityType === EntityType.CONTACT) {
            this.entityType = EntityType.CONTACT;
            this.avatarUrl = apiResult.contactInfo.avatarUrl;
            this.firstName = apiResult.contactInfo.firstName;
            this.lastName = apiResult.contactInfo.lastName;
            this.contactName = this.firstName + ' ' + this.lastName;
            this.id = apiResult.contactInfo.id;
            this.isPrivate = !!apiResult.contactInfo.privateContactMetadata;
            this.numberOfEmployments = apiResult.numberOfEmployments;
            if (apiResult.contactInfo.geography) {
                this.locationString = apiResult.contactInfo.geography.displayString;
            } else if (apiResult.outletInfo?.geography) {
                this.locationString = apiResult.outletInfo.geography.displayString;
            } else if (apiResult.contactInfo.employments?.length > 0 && apiResult.contactInfo.employments[0].outletInfo?.geography) {
                this.locationString = apiResult.contactInfo.employments[0].outletInfo.geography.displayString;
            }
        } else {
            this.entityType = EntityType.OUTLET;
            this.name = apiResult.outletInfo.name;
            this.id = apiResult.outletInfo.id;
            this.avatarUrl = apiResult.outletInfo.avatarUrl;

            if (apiResult.outletInfo.coverageFocus) {
                this.coverageFocus = new LocalizedEnum(apiResult.outletInfo.coverageFocus);
            }

            if (apiResult.outletInfo.mediaTypes) {
                this.mediaTypes = apiResult.outletInfo.mediaTypes
                    .filter(x => Object.values(TargetingMediaTypeEnum).includes(x.enumValue as TargetingMediaTypeEnum))
                    .map(x => {
                        x.displayString = `Discover.targeting.filter.mediaType.${x.enumValue as TargetingMediaTypeEnum}`;

                        return new LocalizedEnum(x);
                    });
            }

            if (apiResult.outletInfo.geography) {
                this.locationString = apiResult.outletInfo.geography.displayString;
            }

            if (apiResult.outletInfo.circulation.length > 0) {
                this.circulations = apiResult.outletInfo.circulation.map(x => new CirculationModel(x));
                this.circulations = this.circulations.sort((x1, x2) => x2.value - x1.value);

                /* if (circulations.length > 0) {
                    if (circulations.find(x => x.circulationType.enumValue === CirculationType.manualValue)) {
                        this.uniqueVisitors = circulations.find(x => x.circulationType.enumValue === CirculationType.manualValue);
                    } else {
                        this.uniqueVisitors = circulations.find(x => x.circulationType.enumValue === CirculationType.partnerValue);
                    }
                } */
            }
        }
    }

    toEntityIdModel(): EntityIdApiModel {
        // Outlet card
        if (this.entityType === EntityType.OUTLET) {
            return {
                outletUuid: this.id
            };
        // Contact card
        } else if (this.selectedEmployment) {
            return {
                contactUuid: this.id,
                employmentId: this.selectedEmployment.id
            };
        } else if (this.hasActiveNotice) {
            return {
                contactUuid: this.id
            };
        // Outlet's staff subcard
        } else {
            return {
                contactUuid: this.contactId,
                employmentId: +this.id
            };
        }
    }

    // TODO: Needed as is? Or inline? Or inside Factory function?
    setBadges() {
        if (this.entityType === EntityType.CONTACT) {
            this.badges = this.contactModel.badges;

            return;
        }

        this.badges = this.outletModel.badges;
    }

    // since we don't want a function in the HTML to hide/show the remove from dist button,
    // I decided to add it on the model, so this functions loops through the selected distributions
    // to see if it's already part of the dist
    // it also sets the tooltip dist names
    hideShowRemoveDistribution(distributions: DistributionSummaryModel[]) {
        this.showRemoveDistribution = false;
        this.removeDistributionTooltips = '';
        distributions?.forEach(distribution => {
            if (this.listMemberships?.findIndex(list => list.id === distribution.individualRecipientsListId) >= 0) {
                this.showRemoveDistribution = true;
                this.removeDistributionTooltips += distribution.name + ', ';
                return;
            }
        });
        if (this.removeDistributionTooltips.endsWith(', ')) {
            this.removeDistributionTooltips = this.removeDistributionTooltips.replace(new RegExp(', ' + '$'), '');
        }
    }
}

